import { Link, useParams, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();

  const activeSlug = slug || "re-xue";
  const activeCategory = categories.find((c) => c.slug === activeSlug);

  let filtered = [...articles];
  if (q) {
    const kw = q.toLowerCase();
    filtered = filtered.filter(
      (a) =>
        a.title.toLowerCase().includes(kw) ||
        a.summary.toLowerCase().includes(kw) ||
        a.tags.some((t) => t.toLowerCase().includes(kw)) ||
        a.category.includes(q)
    );
  } else if (activeCategory) {
    filtered = filtered.filter((a) => a.categorySlug === activeCategory.slug);
  }

  const pageTitle = q
    ? `搜索"${q}" - 动漫网`
    : activeCategory
      ? `${activeCategory.name}动漫 - 动漫网`
      : "全部分类 - 动漫网";
  const pageDesc = q
    ? `动漫网搜索"${q}"的结果，为你聚合匹配的动漫资讯与文章。`
    : activeCategory
      ? `动漫网${activeCategory.name}动漫频道，聚合${activeCategory.desc}相关的最新动漫与资讯。`
      : "动漫网全部分类，浏览热血、恋爱、治愈、科幻等八大动漫频道。";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: pageTitle,
      url: `https://animelist8.github.io/category/${activeSlug}`,
      description: pageDesc,
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: "动漫网", url: "https://animelist8.github.io/" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "动漫网", item: "https://animelist8.github.io/" },
        { "@type": "ListItem", position: 2, name: activeCategory ? `${activeCategory.name}动漫` : "分类频道" },
      ],
    },
  ];

  return (
    <>
      <Seo title={pageTitle} description={pageDesc} canonical={`/category/${activeSlug}`} type="collection" jsonLd={jsonLd} />

      <section className="bg-background-100 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "动漫网", to: "/" }, { label: "分类频道", to: "/category" }]} />
          <div className="mt-4">
            <h1 className="font-heading text-2xl text-foreground-900 md:text-4xl">
              {q ? `搜索"${q}"` : activeCategory ? `${activeCategory.name}动漫` : "动漫分类频道"}
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
              {q ? `为你找到 ${filtered.length} 条与"${q}"相关的动漫资讯与文章。` : activeCategory ? activeCategory.desc : "浏览全部动漫分类频道与内容"}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/category/all"
              className={`inline-flex items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                !activeCategory ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                  activeCategory?.slug === c.slug ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={`${c.icon} text-xs`}></i>
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-base text-foreground-600">暂时没有找到相关内容，换个关键词试试吧</p>
              <Link to="/category" className="mt-5 inline-flex items-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600">
                返回全部频道
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}